import React, { useState, useEffect, useCallback } from 'react';
import { BellRinging, CircleNotch, X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLE = {
  info:    { dot: 'bg-blue-500',   chip: 'text-blue-700' },
  warning: { dot: 'bg-amber-500',  chip: 'text-amber-700' },
  success: { dot: 'bg-green-500',  chip: 'text-green-700' },
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchCount = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/inapp-notifications/unread-count`, { credentials: 'include' });
      if (r.ok) setCount((await r.json()).count || 0);
    } catch (e) { /* silent */ }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/inapp-notifications?limit=20`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setItems(Array.isArray(data) ? data : (data?.notifications || []));
      } else {
        setItems([]);
      }
    } catch (e) { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [fetchCount]);

  const togglePanel = async () => {
    if (!open) await fetchItems();
    setOpen(!open);
  };

  const markRead = async (id) => {
    try {
      await fetch(`${API}/api/inapp-notifications/${id}/read`, { method: 'POST', credentials: 'include' });
      setItems(arr => arr.map(n => n.id === id ? { ...n, read: true } : n));
      setCount(c => Math.max(0, c - 1));
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/api/inapp-notifications/read-all`, { method: 'POST', credentials: 'include' });
      setItems(arr => arr.map(n => ({ ...n, read: true })));
      setCount(0);
    } catch (e) { /* silent */ }
  };

  const handleClick = async (n) => {
    if (!n.read) await markRead(n.id);
    setOpen(false);
    if (n.lead_id) navigate(`/leads/${n.lead_id}`);
  };

  return (
    <div className="relative">
      <button onClick={togglePanel}
              className="relative p-2 hover:bg-slate-100 rounded-sm transition-colors"
              title={`${count} notifiche non lette`}
              data-testid="inapp-notifications-button">
        <BellRinging size={20} className="text-slate-600" weight={count > 0 ? 'fill' : 'regular'} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                data-testid="inapp-notifications-badge">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden"
               data-testid="inapp-notifications-panel">
            <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-slate-50">
              <span className="font-semibold text-sm text-slate-900">Notifiche</span>
              <div className="flex items-center gap-2">
                {count > 0 && (
                  <button onClick={markAllRead}
                          className="text-xs text-slate-600 hover:text-slate-900"
                          data-testid="inapp-mark-all-read">
                    Segna tutte come lette
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  <CircleNotch size={20} className="inline animate-spin" />
                </div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  Nessuna notifica al momento
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {items.map(n => {
                    const st = SEVERITY_STYLE[n.severity] || SEVERITY_STYLE.info;
                    const dt = n.created_at ? new Date(n.created_at) : null;
                    return (
                      <li key={n.id}
                          onClick={() => handleClick(n)}
                          className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${!n.read ? 'bg-amber-50/30' : ''}`}
                          data-testid={`notification-item-${n.id}`}>
                        <div className="flex items-start gap-2">
                          <span className={`mt-1 w-2 h-2 rounded-full ${st.dot} ${!n.read ? '' : 'opacity-40'}`} />
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-semibold ${!n.read ? 'text-slate-900' : 'text-slate-600'}`}>
                              {n.title}
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</div>
                            <div className="text-[10px] text-slate-400 mt-1">
                              {dt ? dt.toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
